import {
  ActivityIndicator,
  Pressable,
  SectionList,
  Text,
  TextInput,
  View,
  type SectionListRenderItem,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  EquipmentKind,
  MuscleRegion,
  type Exercise,
} from '@heraion/shared';

import { FiltersToggle, SectionHeader } from '../index';

import type { WeightUnit } from './state/loggerDraft';
import type { DraftEntry } from './state/loggerDraftTypes';
import { useTheme } from './theme';
import type { makeStyles } from './ExerciseSwapPicker.styles';
import { FilterChipsRow } from './_FilterChipsRow';
import { ExerciseSwapPickerRow } from './_ExerciseSwapPickerRow';
import { UnavailableCandidateRow } from './_UnavailableCandidateRow';
import type { SwapSection, useSwapPickerFilters } from './_useSwapPickerFilters';

// DS-42 band 3 — "switch to another exercise": search, the filter axes and the
// candidate list. Split out of the screen so the orchestrator reads as the
// three bands it now is, and so the file that gained two headings could give
// back more than it took.
//
// ⚠️ The boxed Suggeriti / Tutti headers INSIDE this list stay exactly as they
// were. They are a different object from the band heading above them: they
// separate groups within one list, the band heading names a region of the
// screen. Making them look alike is the confusion DS-42 exists to remove.
type Props = {
  filters: ReturnType<typeof useSwapPickerFilters>;
  catalog: Exercise[] | null;
  onPick: (next: Exercise, instance?: string | null) => void;
  orderByLocation: string | null;
  liveEntries: readonly DraftEntry[];
  unit: WeightUnit;
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  PainDot?: import('react').ComponentType<{ exercise: Exercise }>;
  usePainRiskLabel?: (exercise: Exercise) => string | null;
  /** EQ-1-GYM-COLDSTART — exercises with nothing left to train them on here.
   *  They are already last (the filters hook sank them); this is what marks
   *  them and offers the ✚. */
  sunk: ReadonlySet<string>;
  /** Undoes the declaration for one exercise at this place. */
  onRestore: (exercise: Exercise) => void;
  /** R6 — the machines declared absent here, resolved per exercise. Narrows
   *  what each row's gear tray may offer; never what the list shows. */
  absentInstances: (exercise: string) => readonly string[];
  /** EQ-1-PIN-DEFAULT — the elected winner per candidate exercise, so a row
   *  the athlete already decided does not re-open its tray. */
  pinnedFor?: (exercise: string) => string | null | undefined;
};

export function SwapPickerCandidateList({
  filters,
  catalog,
  onPick,
  orderByLocation,
  liveEntries,
  unit,
  theme,
  styles,
  PainDot,
  usePainRiskLabel,
  sunk,
  onRestore,
  absentInstances,
  pinnedFor,
}: Props) {
  const { t } = useTranslation();

  const renderItem: SectionListRenderItem<Exercise, SwapSection> = ({
    item,
    section,
  }) => {
    const row = (
      <ExerciseSwapPickerRow
        exercise={item}
        reasonChips={
          section.key === 'suggested'
            ? filters.reasonChips(section.candidates.get(item.slug))
            : []
        }
        onPick={onPick}
        PainDot={PainDot}
        usePainRiskLabel={usePainRiskLabel}
        orderByLocation={orderByLocation}
        liveEntries={liveEntries}
        unit={unit}
        theme={theme}
        styles={styles}
        absentHere={absentInstances(item.slug)}
        {...(() => {
          const p = pinnedFor?.(item.slug);
          return p !== undefined ? { pinned: p } : {};
        })()}
      />
    );
    // The row itself is UNCHANGED when it is sunk — it is still tappable, and
    // picking it still works. Being declared missing is a statement about this
    // place, not a lock on the exercise.
    return sunk.has(item.slug) ? (
      <UnavailableCandidateRow
        onRestore={() => onRestore(item)}
        theme={theme}
        styles={styles}
      >
        {row}
      </UnavailableCandidateRow>
    ) : (
      row
    );
  };

  const renderSectionHeader = ({ section }: { section: SwapSection }) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{section.title}</Text>
      <Text style={styles.sectionCount}>{section.data.length}</Text>
    </View>
  );

  return (
    <>
      <SectionHeader
        theme={theme}
        title={t('swap.bandOtherExercises', {
          defaultValue: 'Switch to another exercise',
        })}
        style={styles.bandHeader}
      />
      {/* Same shape as the add picker: search and the filters control on one
          line, the axes only when asked for. Two permanently-open chip rows
          were eating the top of the list on a phone. */}
      <View style={styles.searchWrap}>
        <TextInput
          style={styles.search}
          placeholder={t('swap.searchPlaceholder')}
          placeholderTextColor={theme.palette.ink3}
          autoCorrect={false}
          autoCapitalize="none"
          value={filters.search}
          onChangeText={filters.setSearch}
        />
        <FiltersToggle
          theme={theme}
          open={filters.filtersOpen}
          onToggle={() => filters.setFiltersOpen(!filters.filtersOpen)}
          activeCount={filters.regions.size + filters.equipment.size}
          t={t}
        />
      </View>
      {filters.filtersOpen ? (
        <>
          <FilterChipsRow
            label={t('catalog.region')}
            options={MuscleRegion.options}
            active={filters.regions}
            onToggle={filters.toggleRegion}
            renderLabel={(region) =>
              t(`catalog.region_${region}`, { defaultValue: region })
            }
          />
          <FilterChipsRow
            label={t('catalog.equipment')}
            options={EquipmentKind.options}
            active={filters.equipment}
            onToggle={filters.toggleEquipment}
            renderLabel={(kind) =>
              t(`catalog.equipment_${kind}`, { defaultValue: kind })
            }
          />
        </>
      ) : null}
      {filters.hasFilters ? (
        <Pressable
          accessibilityRole="button"
          onPress={filters.clearFilters}
          style={({ pressed }) => [
            styles.clearFilters,
            pressed && styles.clearFiltersPressed,
          ]}
        >
          <Text style={styles.clearFiltersText}>
            {t('mobile.logger.pickerClearFilters')}
          </Text>
        </Pressable>
      ) : null}
      {catalog === null ? (
        <View style={styles.center}>
          <ActivityIndicator color={theme.palette.ink2} />
        </View>
      ) : filters.empty ? (
        <View style={styles.center}>
          <Text style={styles.emptyText}>
            {filters.hasFilters
              ? t('swap.noMatches')
              : t('swap.noSuggestions')}
          </Text>
        </View>
      ) : (
        <SectionList
          sections={filters.sections}
          keyExtractor={(ex) => ex.slug}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          stickySectionHeadersEnabled={false}
          ItemSeparatorComponent={() => (
            <View
              style={[
                styles.separator,
                { backgroundColor: theme.palette.border },
              ]}
            />
          )}
          keyboardShouldPersistTaps="handled"
        />
      )}
    </>
  );
}
